"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@workspace/ui/components/button";
import { DialogDescription, DialogHeader, DialogTitle } from "@workspace/ui/components/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/ui/components/select";
import { CheckCircle2 } from "lucide-react";
import posthog from "posthog-js";
import { useId, useRef, useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { addLeadDetails, submitLead } from "@/app/actions";
import { adsManagementOptions, adsManagementSelectOptions, LeadSchema, monthlyAdSpendOptions, monthlyAdSpendSelectOptions, productCategorySelectOptions, type LeadDetailsInput, type LeadInput } from "@/lib/lead-types";
import { trackMetaLead } from "@/lib/track-meta-lead";
import { readLeadSource } from "./attribution";
import { HERO, LEAD_FORM, type LeadFormLocation } from "./content";
const fieldClassName = "border-white/20 bg-white/10 text-white placeholder:text-white/50 focus-visible:border-[#E5E244] focus-visible:ring-[#E5E244]/30";
const selectTriggerClassName = "border-white/20 bg-white/10 text-white focus-visible:border-[#E5E244] focus-visible:ring-[#E5E244]/30";
const primaryButtonClassName = "w-full border border-[#262321] bg-[#E5E244] py-6 text-base font-bold text-[#262321] hover:bg-[#E5E244]/90";
const DetailsFormSchema = z.object({
  adsManagement: z.string().min(1, {
    message: "Selecciona una opción"
  }).pipe(z.enum(adsManagementOptions)),
  monthlyAdSpend: z.string().min(1, {
    message: "Selecciona un rango"
  }).pipe(z.enum(monthlyAdSpendOptions))
});
export type LeadFormStage = {
  name: "contact";
} | {
  name: "details";
  leadPublicId: string | null;
} | {
  name: "not_fashion";
} | {
  name: "done";
};
export function LeadForm(t0) {
  const $ = _c(15);
  const {
    onStageChange,
    placement,
    stage
  } = t0;
  if (stage.name === "contact") {
    let t1;
    let t2;
    if ($[0] !== onStageChange) {
      t1 = leadPublicId => onStageChange({
        name: "details",
        leadPublicId
      });
      t2 = () => onStageChange({
        name: "not_fashion"
      });
      $[0] = onStageChange;
      $[1] = t1;
      $[2] = t2;
    } else {
      t1 = $[1];
      t2 = $[2];
    }
    let t3;
    if ($[3] !== placement || $[4] !== t1 || $[5] !== t2) {
      t3 = <ContactStep placement={placement} onQualified={t1} onRejected={t2} />;
      $[3] = placement;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (stage.name === "details") {
    let t1;
    if ($[7] !== onStageChange) {
      t1 = () => onStageChange({
        name: "done"
      });
      $[7] = onStageChange;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== placement || $[10] !== stage.leadPublicId || $[11] !== t1) {
      t2 = <DetailsStep leadPublicId={stage.leadPublicId} placement={placement} onFinished={t1} />;
      $[9] = placement;
      $[10] = stage.leadPublicId;
      $[11] = t1;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  if (stage.name === "not_fashion") {
    let t1;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="text-center"><DialogHeader className="sm:text-center"><DialogTitle className="text-xl text-white">{LEAD_FORM.notFashionTitle}</DialogTitle><DialogDescription className="text-stone-300">{LEAD_FORM.notFashionSubtitle}</DialogDescription></DialogHeader></div>;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    return t1;
  }
  let t1;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-center"><DialogHeader className="sm:text-center"><CheckCircle2 className="mx-auto h-12 w-12 text-[#E5E244]" /><DialogTitle className="mt-4 text-xl text-white">{LEAD_FORM.doneTitle}</DialogTitle><DialogDescription className="text-stone-300">{LEAD_FORM.doneSubtitle}</DialogDescription></DialogHeader></div>;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  return t1;
}

/**
 * Asks only for what we need to start a WhatsApp conversation and stores the
 * lead right away, so dropping off later still leaves us a contactable lead.
 */
function ContactStep(t0) {
  const $ = _c(41);
  const {
    onQualified,
    onRejected,
    placement
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(LeadSchema),
      defaultValues: {
        name: "",
        productCategory: "",
        website: "",
        whatsapp: ""
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const form = useForm(t1);
  const [error, setError] = useState(null);
  const [isPending, startTransition] = useTransition();
  const hasStarted = useRef(false);
  const honeypotId = useId();
  let t2;
  if ($[1] !== placement) {
    t2 = function handleFirstInteraction() {
      if (hasStarted.current) {
        return;
      }
      hasStarted.current = true;
      posthog.capture("business_lead_form_started", {
        placement
      });
    };
    $[1] = placement;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleFirstInteraction = t2;
  let t3;
  if ($[3] !== placement) {
    t3 = function onInvalid(errors) {
      posthog.capture("business_lead_form_invalid", {
        fields: Object.keys(errors),
        placement
      });
    };
    $[3] = placement;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onInvalid = t3;
  let t4;
  if ($[5] !== honeypotId || $[6] !== onQualified || $[7] !== onRejected || $[8] !== placement) {
    t4 = function onSubmit(values) {
      setError(null);
      startTransition(async () => {
        const source = readLeadSource();
        const honeypot = (document.getElementById(honeypotId) as HTMLInputElement | null)?.value;
        const result = await submitLead({
          ...values,
          source,
          honeypot,
          pageUrl: window.location.href
        });
        if (!result.ok) {
          posthog.capture("business_lead_form_failed", {
            placement,
            reason: result.error
          });
          setError(result.error);
          return;
        }
        if (result.leadPublicId == null) {
          onQualified(null);
          return;
        }
        if (result.fashionFit === "not_fashion") {
          posthog.capture("business_lead_rejected_not_fashion", {
            placement,
            product_category: values.productCategory,
            referrer: source.referrer ?? null,
            utm_campaign: source.utmCampaign ?? null,
            utm_medium: source.utmMedium ?? null,
            utm_source: source.utmSource ?? null
          });
          onRejected();
          return;
        }
        if (result.metaEventId) {
          trackMetaLead({
            eventId: result.metaEventId,
            name: values.name,
            whatsapp: values.whatsapp
          });
        }
        posthog.capture("business_lead_submitted", {
          fashion_fit: result.fashionFit,
          placement,
          product_category: values.productCategory,
          referrer: source.referrer ?? null,
          utm_campaign: source.utmCampaign ?? null,
          utm_medium: source.utmMedium ?? null,
          utm_source: source.utmSource ?? null
        });
        onQualified(result.leadPublicId);
      });
    };
    $[5] = honeypotId;
    $[6] = onQualified;
    $[7] = onRejected;
    $[8] = placement;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onSubmit = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogHeader><DialogTitle className="text-white">{HERO.formTitle}</DialogTitle><DialogDescription className="text-stone-400">{HERO.formSubtitle}</DialogDescription></DialogHeader>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== form || $[12] !== onInvalid || $[13] !== onSubmit) {
    t6 = form.handleSubmit(onSubmit, onInvalid);
    $[11] = form;
    $[12] = onInvalid;
    $[13] = onSubmit;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== honeypotId) {
    t7 = <input id={honeypotId} type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden={true} className="pointer-events-none absolute h-0 w-0 opacity-0" />;
    $[15] = honeypotId;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[17] !== form.control) {
    t8 = <FormField control={form.control} name="name" render={_temp} />;
    t9 = <FormField control={form.control} name="whatsapp" render={_temp2} />;
    t10 = <FormField control={form.control} name="website" render={_temp3} />;
    t11 = <FormField control={form.control} name="productCategory" render={_temp5} />;
    $[17] = form.control;
    $[18] = t10;
    $[19] = t11;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[22] !== error) {
    t12 = error && <p className="text-sm text-red-300">{error}</p>;
    $[22] = error;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = isPending ? LEAD_FORM.submittingCta : LEAD_FORM.submitCta;
  let t14;
  if ($[24] !== isPending || $[25] !== t13) {
    t14 = <Button type="submit" disabled={isPending} size="lg" className={`mt-2 ${primaryButtonClassName}`}>{t13}</Button>;
    $[24] = isPending;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="text-center text-xs text-stone-400">{LEAD_FORM.reassurance}</p>;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== handleFirstInteraction || $[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t14 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t16 = <form onSubmit={t6} onFocus={handleFirstInteraction} className="space-y-4 text-left" noValidate={true}>{t7}{t8}{t9}{t10}{t11}{t12}{t14}{t15}</form>;
    $[28] = handleFirstInteraction;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t14;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== form || $[39] !== t16) {
    t17 = <>{t5}<Form {...form}>{t16}</Form></>;
    $[38] = form;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}

/**
 * Ads context asked once the lead is already saved. Both answers are required
 * here; dropping off still leaves the contactable lead from the first step.
 */
function _temp5(t0) {
  const {
    field: field_2
  } = t0;
  return <FormItem><FormLabel className="text-white">¿Qué vendés?</FormLabel><Select value={field_2.value || undefined} onValueChange={field_2.onChange}><FormControl><SelectTrigger className={selectTriggerClassName}><SelectValue placeholder={"Selecciona una categor\xEDa"} /></SelectTrigger></FormControl><SelectContent>{productCategorySelectOptions.map(_temp4)}</SelectContent></Select><FormMessage /></FormItem>;
}
function _temp4(option) {
  return <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>;
}
function _temp3(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem><FormLabel className="text-white">Web de tu tienda</FormLabel><FormControl><Input inputMode="url" placeholder="www.mitienda.com" autoComplete="url" className={fieldClassName} {...field_1} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel className="text-white">WhatsApp</FormLabel><FormControl><Input type="tel" inputMode="tel" placeholder="099 123 456" autoComplete="tel" className={fieldClassName} {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel className="text-white">Nombre</FormLabel><FormControl><Input placeholder="Tu nombre" autoComplete="name" className={fieldClassName} {...field} /></FormControl><FormMessage /></FormItem>;
}
function DetailsStep(t0) {
  const $ = _c(26);
  const {
    leadPublicId,
    onFinished,
    placement
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(DetailsFormSchema),
      defaultValues: {
        adsManagement: "",
        monthlyAdSpend: ""
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const form = useForm(t1);
  const [isPending, startTransition] = useTransition();
  let t2;
  if ($[1] !== placement) {
    t2 = function onInvalid(errors) {
      posthog.capture("business_lead_form_invalid", {
        fields: Object.keys(errors),
        placement,
        step: "details"
      });
    };
    $[1] = placement;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onInvalid = t2;
  let t3;
  if ($[3] !== leadPublicId || $[4] !== onFinished || $[5] !== placement) {
    t3 = function onSubmit(values) {
      startTransition(async () => {
        if (leadPublicId) {
          await addLeadDetails({
            leadPublicId,
            adsManagement: values.adsManagement,
            monthlyAdSpend: values.monthlyAdSpend
          });
        }
        posthog.capture("business_lead_details_submitted", {
          ads_management: values.adsManagement,
          monthly_ad_spend: values.monthlyAdSpend,
          placement
        });
        onFinished();
      });
    };
    $[3] = leadPublicId;
    $[4] = onFinished;
    $[5] = placement;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onSubmit = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DialogHeader><DialogTitle className="text-white">{LEAD_FORM.detailsTitle}</DialogTitle><DialogDescription className="text-stone-400">{LEAD_FORM.detailsIntro}</DialogDescription></DialogHeader>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== form || $[9] !== onInvalid || $[10] !== onSubmit) {
    t5 = form.handleSubmit(onSubmit, onInvalid);
    $[8] = form;
    $[9] = onInvalid;
    $[10] = onSubmit;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  let t7;
  if ($[12] !== form.control) {
    t6 = <FormField control={form.control} name="adsManagement" render={_temp7} />;
    t7 = <FormField control={form.control} name="monthlyAdSpend" render={_temp9} />;
    $[12] = form.control;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  const t8 = isPending ? "Guardando..." : "Enviar";
  let t9;
  if ($[15] !== isPending || $[16] !== t8) {
    t9 = <Button type="submit" disabled={isPending} size="lg" className={primaryButtonClassName}>{t8}</Button>;
    $[15] = isPending;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t9) {
    t10 = <form onSubmit={t5} className="space-y-4 text-left" noValidate={true}>{t6}{t7}{t9}</form>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== form || $[24] !== t10) {
    t11 = <>{t4}<Form {...form}>{t10}</Form></>;
    $[23] = form;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp9(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel className="text-white">Inversión mensual aprox. en ads</FormLabel><Select value={field_0.value || undefined} onValueChange={field_0.onChange}><FormControl><SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Selecciona un rango" /></SelectTrigger></FormControl><SelectContent>{monthlyAdSpendSelectOptions.map(_temp8)}</SelectContent></Select><FormMessage /></FormItem>;
}
function _temp8(option_0) {
  return <SelectItem key={option_0.value} value={option_0.value}>{option_0.label}</SelectItem>;
}
function _temp7(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel className="text-white">¿Quién maneja tus ads hoy?</FormLabel><Select value={field.value || undefined} onValueChange={field.onChange}><FormControl><SelectTrigger className={selectTriggerClassName}><SelectValue placeholder={"Selecciona una opci\xF3n"} /></SelectTrigger></FormControl><SelectContent>{adsManagementSelectOptions.map(_temp6)}</SelectContent></Select><FormMessage /></FormItem>;
}
function _temp6(option) {
  return <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>;
}